<template>
  <header class="header">
    <div class="container">
      <div class="header__wrapper">
        <div class="header__left">
          <div class="header__dropdown" ref="dropdownRef">
            <svg @click="setSwitcherVisible(!isSwitcherVisible)" class="header__dropdown-button" :class="[{ active: isSwitcherVisible }]" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect width="48" height="48" rx="10" fill="#F3F9FB"/>
              <line x1="12.75" y1="15.25" x2="37.25" y2="15.25" stroke="#008ECC" stroke-width="1.5" stroke-linecap="round"/>
              <line x1="12.75" y1="24.25" x2="30.1591" y2="24.25" stroke="#008ECC" stroke-width="1.5" stroke-linecap="round"/>
              <line x1="12.75" y1="33.25" x2="24.25" y2="33.25" stroke="#008ECC" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <div
                class="header__dropdown-menu"
                :class="[{active: isSwitcherVisible}]"
            >
              <ul class="header__dropdown-menu-wrapper">
                <li
                    v-for="category in categories"
                    :key="category.node.uuid"
                    @click="redirect(category.node.slug)"
                    class="header__dropdown-item"
                    :class="[{ active: activeCategory === category.node.slug }]"
                >
                  {{ category.node.name }}
                </li>
              </ul>
            </div>
          </div>
          <router-link class="header__logo" :to="translations.i18nRoute({ name: 'home' })">
            <img src="@images/logo.png" alt="logo">
          </router-link>
          <form class="header__form" @submit.prevent="search">
            <input
                class="header__form-input"
                :placeholder="t('fields.headerSearch')"
                v-model="searchInput"
            />
          </form>
        </div>
        <svg class="header__menu" @click="isMenuActive = !isMenuActive" width="40px" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" fill="none">
          <path fill="currentColor" fill-rule="evenodd"
                d="M18 5a1 1 0 100-2H2a1 1 0 000 2h16zm0 4a1 1 0 100-2h-8a1 1 0 100 2h8zm1 3a1 1 0 01-1 1H2a1 1 0 110-2h16a1 1 0 011 1zm-1 5a1 1 0 100-2h-8a1 1 0 100 2h8z"/>
        </svg>
        <div class="header__actions" v-if="user">
          <router-link :to="translations.i18nRoute({ name: 'profile-balance' })" class="header__actions-item">
            <img src="@icons/feathers.svg" alt="feathers">
            {{ userBalance }}
          </router-link>
          <router-link :to="translations.i18nRoute({ name: 'wishlist' })" class="header__actions-item">
            <el-badge :value="productsInWishlistQuantity">
              <img src="@icons/wishlistIcon.svg" :alt="t('buttons.wishlist')">
            </el-badge>
            {{ t('buttons.wishlist') }}
          </router-link>
          <router-link :to="translations.i18nRoute({ name: 'cart' })" class="header__actions-item">
            <el-badge :value="productsInCartQuantity">
              <img src="@icons/cartIcon.svg" :alt="t('buttons.cart')">
            </el-badge>
            {{ t('buttons.cart') }}
          </router-link>
          <router-link :to="translations.i18nRoute({ name: 'profile-settings' })" class="header__actions-item">
            <img src="@icons/userIcon.svg" :alt="t('profile.title')">
            {{ t('profile.title') }}
          </router-link>
        </div>
        <router-link v-else :to="translations.i18nRoute({ name: 'login' })" class="header__auth">
          <img src="@icons/userIcon.svg" alt="user">
          <p>{{ t('header.auth') }}</p>
        </router-link>
      </div>
    </div>
    <base-menu :categories="categories" :isActive="isMenuActive" @close-modal="handleCloseModal" />
  </header>
</template>

<script setup>
import translations from "@/core/helpers/translations.js";
import {computed, ref} from "vue";
import {useCategoryStore} from "@/stores/category.js";
import {useRoute, useRouter} from "vue-router";
import {onClickOutside} from "@vueuse/core";
import {useI18n} from "vue-i18n";
import {useAuthStore} from "@/stores/auth.js";
import {useCartStore} from "@/stores/cart.js";
import {useWishlistStore} from "@/stores/wishlist.js";
import BaseMenu from "@/components/base/base-menu.vue";

const categoryStore = useCategoryStore()
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const router = useRouter()
const route = useRoute()
const {t} = useI18n()

const categories = computed(() => categoryStore.categories)

const activeCategory = computed(() => {
  return route.query.categoriesSlugs
})
const user = computed(() => {
  return authStore.user
})
const userBalance = computed(() => {
  return authStore.user ? authStore.user.balance.amount : 0
})

const productsInCartQuantity = computed(() => {
  let count = 0
  cartStore.currentOrder.orderProducts?.edges.forEach((el) => {
    count = count + el.node.quantity
  })

  return count
})
const productsInWishlistQuantity = computed(() => {
  return wishlistStore.wishlist.products ? wishlistStore.wishlist.products.edges.length : 0
})

const redirect = (categorySlug) => {
  router.push({
    name: 'market',
    query: {
      categoriesSlugs: categorySlug
    },
    params: {
      locale: localStorage.getItem('nest-user-locale') || 'en-gb'
    }
  })
  isSwitcherVisible.value = false
}

const searchInput = ref('')
const search = () => {
  router.push({
    name: 'search',
    params: {
      searchQuery: searchInput.value,
      locale: localStorage.getItem('nest-user-locale') || 'en-gb'
    }
  })
}

const isSwitcherVisible = ref(false)
const setSwitcherVisible = (state) => {
  isSwitcherVisible.value = state
}

const dropdownRef = ref(null)
onClickOutside(dropdownRef, () => isSwitcherVisible.value = false)

const isMenuActive = ref(false)
const handleCloseModal = () => {
  isMenuActive.value = false;
}
</script>

<style lang="scss" scoped>
.header {
  border-bottom: 1px solid #EDEDED;
  background-color: $white;
  position: fixed;
  width: 100vw;
  z-index: 10;

  &__wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  &__dropdown {
    position: relative;

    &-button {
      cursor: pointer;
      transition: 0.2s;

      & rect {
        transition: 0.2s;
      }
      & line {
        transition: 0.2s;
      }

      &.active {
        & rect {
          fill: #008ECC;
        }

        & line {
          stroke: #F3F9FB;
        }
      }

      @include hover {
        & rect {
          fill: #008ECC;
        }

        & line {
          stroke: #F3F9FB;
        }
      }
    }

    &-menu {
      position: absolute;
      top: 110%;
      left: 0;
      border-radius: 10px;
      box-shadow: 0 4px 8.2px 0 rgba(0, 0, 0, 0.10);
      backdrop-filter: blur(6px);
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows 0.2s ease;
      overflow: hidden;

      &-wrapper {
        display: flex;
        flex-direction: column;
      }

      &.active {
        grid-template-rows: 1fr;
      }

      & > * {
        min-height: 0;
      }
    }

    &-item {
      cursor: pointer;
      transition: 0.2s;
      padding: 5px 15px;
      background-color: #b7b7b7;

      color: #222;
      font-size: 16px;
      font-weight: 500;
      line-height: 26px;

      @include hover {
        background-color: #008ECC;
        color: $white;
      }

      &.active {
        background-color: #008ECC;
        color: $white;
      }
    }
  }

  &__logo {
    & img {
      width: 140px;
    }
  }

  &__form {
    width: 400px;
    margin-left: 60px;

    &-input {
      width: 100%;
      border-radius: 10px;
      background-color: #F3F9FB;
      padding: 15px 50px;
      background-image: url(@icons/searchIcon.svg);
      background-repeat: no-repeat;
      background-position: left 13px top 50%;

      color: #666;
      font-size: 14px;
      line-height: 18px;
    }
  }

  &__menu {
    flex-shrink: 0;
    cursor: pointer;
    transition: 0.2s;
    color: #008ECC;
    display: none;

    @include hover {
      color: #0077a9;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 15px;

    &-item {
      cursor: pointer;
      padding-right: 15px;
      border-right: 1px solid #D9D9D9;
      display: flex;
      align-items: center;
      gap: 5px;
      transition: 0.2s;

      color: #666;
      font-weight: 700;
      line-height: 18px;

      @include hover {
        color: #008ECC;
      }

      &:last-child {
        border-right: none;
        padding-right: 0;
      }
    }
  }

  &__auth {
    display: flex;
    align-items: center;
    gap: 5px;

    & p {
      transition: 0.2s;
      color: #666;
      font-size: 16px;
      font-weight: 700;
      line-height: 18px;
    }

    @include hover {
      & p {
        color: #008ECC;
      }
    }
  }
}

@media (max-width: 1300px) {
  .header {
    &__wrapper {
      gap: 50px;
    }

    &__left {
      width: 100%;
    }

    &__form {
      margin-left: 0;
      width: 100%;

      &-input {
        padding-inline: 40px 10px;
      }
    }
  }
}

@media (max-width: 1000px) {
  .header {
    &__menu {
      display: block;
    }

    &__actions {
      display: none;
    }

    &__auth {
      display: none;
    }
  }
}

@media (max-width: 650px) {
  .header {
    &__wrapper {
      gap: 5px;
    }

    &__form {
      display: none;
    }
  }
}
</style>